import type { GridSelection } from "@glideapps/glide-data-grid";
import { useEffect, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import type {
  IAiSpreadsheetLoadedSheet,
  IAiSpreadsheetRegion,
  IAiSpreadsheetRegionInput,
} from "@/payroll/ai-script/types";
import { getSheetContentBounds } from "./manual-row-utils";
import { RegionLimitFields } from "./region-limit-fields";
import {
  proposeSpreadsheetRegion,
  validateSpreadsheetRegionDraft,
  type SpreadsheetRegionDraft,
} from "./spreadsheet-region";
import { SpreadsheetRegionColumnControls } from "./spreadsheet-region-column-controls";
import { useRegionLimitFields } from "./use-region-limit-fields";

interface SpreadsheetRegionControlProps {
  sheet: IAiSpreadsheetLoadedSheet;
  selection: GridSelection;
  existingRegion?: IAiSpreadsheetRegion;
  onOpenChange: (open: boolean) => void;
  isSaving: boolean;
  onPreviewChange: (draft: SpreadsheetRegionDraft | null) => void;
  onCreate: (region: IAiSpreadsheetRegionInput) => Promise<void>;
  onUpdate: (
    regionId: string,
    region: IAiSpreadsheetRegionInput,
  ) => Promise<void>;
  onDelete: (regionId: string) => Promise<void>;
}

export const SpreadsheetRegionControl = ({
  sheet,
  selection,
  existingRegion,
  onOpenChange,
  isSaving,
  onPreviewChange,
  onCreate,
  onUpdate,
  onDelete,
}: SpreadsheetRegionControlProps) => {
  const regions = sheet.regions ?? [];
  const [initialDraft] = useState<SpreadsheetRegionDraft | null>(() =>
    existingRegion
      ? {
          header_row: existingRegion.header_row,
          record_start_row: existingRegion.record_start_row,
          record_end_row: existingRegion.record_end_row,
          start_col: existingRegion.start_col,
          end_col: existingRegion.end_col,
          column_settings: existingRegion.column_settings,
        }
      : proposeSpreadsheetRegion(sheet, selection),
  );
  const [draft, setDraft] = useState(initialDraft);
  const { maxContentRow, maxContentCol } = getSheetContentBounds(sheet.cells);

  // The parent mounts one control per open session. Start its grid preview once,
  // and clear the preview when that session closes.
  useEffect(() => {
    onPreviewChange(initialDraft);
    return () => onPreviewChange(null);
  }, [initialDraft, onPreviewChange]);

  const errors = draft
    ? validateSpreadsheetRegionDraft(draft, {
        regions,
        editedRegionId: existingRegion?.id,
        mergedRanges: sheet.merged_ranges ?? [],
        bounds: { maxRow: maxContentRow, maxCol: maxContentCol },
      })
    : ["Select one cell or a rectangular range first."];

  const limitFields = useRegionLimitFields({
    draft,
    setDraft,
    onPreviewChange,
    errors,
  });
  const { validationErrors } = limitFields;

  const close = () => {
    onOpenChange(false);
  };

  const save = async () => {
    if (!draft || validationErrors.length > 0) return;
    try {
      if (existingRegion) await onUpdate(existingRegion.id, draft);
      else await onCreate(draft);
      close();
    } catch {
      // The parent shows the API validation message. Keep the draft open.
    }
  };

  const remove = async () => {
    if (!existingRegion) return;
    try {
      await onDelete(existingRegion.id);
      close();
    } catch {
      // The parent shows the API error. Keep the dialog open for retry.
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {existingRegion ? "Edit table region" : "Review table region"}
          </DialogTitle>
          <p className="text-xs text-subtle">
            Confirm one header row and the record rows. Review titles, totals,
            extra header rows, and touching tables before you save.
          </p>
        </DialogHeader>

        <Stack
          align="start"
          gap={0}
          className="max-h-[60vh] space-y-5 overflow-y-auto"
        >
          {draft && (
            <>
              <RegionLimitFields draft={draft} fields={limitFields} />

              <SpreadsheetRegionColumnControls
                cells={sheet.cells}
                draft={draft}
                onChange={(nextDraft) => {
                  setDraft(nextDraft);
                  onPreviewChange(nextDraft);
                }}
              />
            </>
          )}

          {validationErrors.length > 0 && (
            <Stack
              align="start"
              gap={0}
              className="rounded-lg bg-danger-subtle p-3 text-xs text-danger"
            >
              {validationErrors.map((error) => (
                <p key={error}>{error}</p>
              ))}
            </Stack>
          )}
          {existingRegion && !existingRegion.validation.valid && (
            <Stack
              align="start"
              gap={0}
              className="rounded-lg bg-danger-subtle p-3 text-xs text-danger"
            >
              {existingRegion.validation.errors.map((error) => (
                <p key={error}>{error}</p>
              ))}
            </Stack>
          )}
        </Stack>

        <DialogFooter>
          {existingRegion && (
            <Button
              variant="secondary"
              color="danger"
              size="sm"
              className="sm:mr-auto"
              disabled={isSaving}
              onClick={() => void remove()}
            >
              Remove table
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={isSaving || !draft || validationErrors.length > 0}
            aria-busy={isSaving || undefined}
            onClick={() => void save()}
          >
            Confirm table
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
